import { computed, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue'
import { sortBy } from 'lodash'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'

const LEGACY_PARENT_GROUP_NAMES = new Set(['Связанные элементы', 'Связанные элемент'])

export function useObjectTypesFieldsGroupsLayout(
  groups: MaybeRefOrGetter<ObjectTypeGroup[]>,
  fields: Ref<ObjectInventoryField[]>,
  trs: (key: string) => string,
) {
  const groupsSort = computed(() => sortBy(toValue(groups), 'sort'))

  const displayGroupName = (group: ObjectTypeGroup) => {
    if (group.type === 'PARENT' || LEGACY_PARENT_GROUP_NAMES.has(group.name)) {
      return trs('itf.relation_element')
    }
    return group.name
  }

  const consolidateFieldGroups = (
    gList: ObjectTypeGroup[],
    currentFields: ObjectInventoryField[],
  ): ObjectInventoryField[] => {
    const infoGroups = gList.filter(
      (group) =>
        group.type === 'GROUP' &&
        group.name !== 'Картографические настройки' &&
        group.name !== 'Настройки поведения',
    )

    const primaryInfoGroup = infoGroups[0]
    if (!primaryInfoGroup || infoGroups.length <= 1) {
      return currentFields
    }

    const otherInfoGroupIds = new Set<number>(
      infoGroups
        .slice(1)
        .map((group) => group.id)
        .filter((id): id is number => typeof id === 'number'),
    )

    return currentFields.map((field) =>
      field.group != null && otherInfoGroupIds.has(field.group)
        ? { ...field, group: primaryInfoGroup.id }
        : field,
    )
  }

  const groupsLayout = computed(() => {
    const gList = groupsSort.value

    const mapGroup = gList.find((group) => group.name === 'Картографические настройки') || null
    const behaviorGroup = gList.find((group) => group.name === 'Настройки поведения') || null
    const infoGroups =
      gList.filter(
        (group) =>
          group.type === 'GROUP' && group.id !== mapGroup?.id && group.id !== behaviorGroup?.id,
      ) || []

    const primaryInfoGroup = infoGroups[0] || null

    const excludeIds = new Set<number>(
      [...infoGroups.map((group) => group.id), mapGroup?.id, behaviorGroup?.id].filter(
        (id): id is number => typeof id === 'number',
      ),
    )

    const listGroups = gList.filter((group) => group.type === 'LIST' && !excludeIds.has(group.id))

    const otherGroups = gList.filter((group) => group.type !== 'LIST' && !excludeIds.has(group.id))

    let primary: ObjectTypeGroup | null = null
    if (primaryInfoGroup) {
      primary = {
        ...primaryInfoGroup,
        name: trs('itf.info'),
      }
    }

    const tabGroups: ObjectTypeGroup[] = []
    tabGroups.push(...listGroups, ...otherGroups)
    if (mapGroup) tabGroups.push(mapGroup)
    if (behaviorGroup) tabGroups.push(behaviorGroup)

    return { primary, tabGroups }
  })

  const groupsView = computed(() => {
    const { primary, tabGroups } = groupsLayout.value
    return [...(primary ? [primary] : []), ...tabGroups]
  })

  watch(
    groupsSort,
    (gList) => {
      const consolidated = consolidateFieldGroups(gList, fields.value)
      if (consolidated !== fields.value) {
        fields.value = consolidated
      }
    },
    { immediate: true },
  )

  return { groupsSort, groupsLayout, groupsView, displayGroupName }
}
